import { keys } from './keys.js';

/**
 * TanStack Query option factories for the resource functions in resources.js.
 * Usage: `useQuery(adminQueries.property(api, id))`. Keys come from `keys` so that
 * mutations can invalidate precisely (`keys.admin.detail('properties', id)`).
 *
 * @typedef {import('./index.js').BlimoApi} BlimoApi
 * @typedef {import('./resources.js').ListParams} ListParams
 */

/** 4xx are answers (not found, forbidden, validation) – never retry them. */
const retry = (/** @type {number} */ count, /** @type {any} */ error) => {
  const status = error?.status;
  if (status && status >= 400 && status < 500) return false;
  return count < 2;
};

export const adminQueries = {
  /** @param {BlimoApi} api */
  dashboard: (api) => ({ queryKey: keys.admin.dashboard(), queryFn: ({ signal }) => api.admin.dashboard({ signal }), retry }),

  /** @param {BlimoApi} api @param {ListParams} params */
  properties: (api, params) => ({
    queryKey: keys.admin.list('properties', params),
    queryFn: ({ signal }) => api.admin.properties.list(params, { signal }),
    placeholderData: keepPrevious,
    retry,
  }),
  /** @param {BlimoApi} api @param {string|number} id */
  property: (api, id) => ({ queryKey: keys.admin.detail('properties', id), queryFn: ({ signal }) => api.admin.properties.get(id, { signal }), retry }),
  /** @param {BlimoApi} api @param {string|number} id @param {{ current?: boolean }} params */
  propertyOwnerships: (api, id, params) => ({
    queryKey: keys.admin.sub('properties', id, 'ownerships', params),
    queryFn: ({ signal }) => api.admin.properties.ownerships(id, params, { signal }),
    retry,
  }),
  /** @param {BlimoApi} api @param {string|number} id @param {{ status?: 'current'|'past' }} params */
  propertyTenancies: (api, id, params) => ({
    queryKey: keys.admin.sub('properties', id, 'tenancies', params),
    queryFn: ({ signal }) => api.admin.properties.tenancies(id, params, { signal }),
    retry,
  }),

  /** Objektrollen; `history` includes ended ones. @param {BlimoApi} api @param {string|number} id @param {{ history?: boolean }} [params] */
  propertyRoles: (api, id, params = {}) => ({
    queryKey: keys.admin.sub('properties', id, 'roles', { history: Boolean(params.history) }),
    queryFn: ({ signal }) => api.admin.properties.roles(id, params, { signal }),
    retry,
  }),
  /** Slim property pick list (filters/pickers) – replaces "first 100 properties". @param {BlimoApi} api @param {{ q?: string, with_archived?: boolean, limit?: number }} [params] */
  propertyLookup: (api, params = {}) => ({
    queryKey: keys.admin.list('property-lookup', params),
    queryFn: ({ signal }) => api.admin.lookup.properties(params, { signal }),
    staleTime: 5 * 60 * 1000,
    retry,
  }),
  /** Global search (⌘K); disabled below 2 characters. @param {BlimoApi} api @param {string} q */
  search: (api, q) => ({
    queryKey: keys.admin.search(q),
    queryFn: ({ signal }) => api.admin.search(q, { signal }),
    enabled: q.trim().length >= 2,
    placeholderData: keepPrevious,
    retry,
  }),
  /** @param {BlimoApi} api @param {ListParams} params */
  tenancies: (api, params) => ({
    queryKey: keys.admin.list('tenancies', params),
    queryFn: ({ signal }) => api.admin.tenancies.list(params, { signal }),
    placeholderData: keepPrevious,
    retry,
  }),
  /** One tenancy incl. `meta.property`. @param {BlimoApi} api @param {string|number} id */
  tenancy: (api, id) => ({ queryKey: keys.admin.detail('tenancies', id), queryFn: ({ signal }) => api.admin.tenancies.get(id, { signal }), retry }),

  /** @param {BlimoApi} api @param {ListParams} params */
  units: (api, params) => ({
    queryKey: keys.admin.list('units', params),
    queryFn: ({ signal }) => api.admin.units.list(params, { signal }),
    placeholderData: keepPrevious,
    retry,
  }),
  /** @param {BlimoApi} api @param {string|number} id */
  unit: (api, id) => ({ queryKey: keys.admin.detail('units', id), queryFn: ({ signal }) => api.admin.units.get(id, { signal }), retry }),

  /** @param {BlimoApi} api @param {ListParams} params */
  parties: (api, params) => ({
    queryKey: keys.admin.list('parties', params),
    queryFn: ({ signal }) => api.admin.parties.list(params, { signal }),
    placeholderData: keepPrevious,
    retry,
  }),
  /** F-103 @param {BlimoApi} api */
  partyStatistics: (api) => ({
    queryKey: keys.admin.list('parties', { statistics: true }),
    queryFn: ({ signal }) => api.admin.parties.statistics({ signal }),
    retry,
  }),
  /** @param {BlimoApi} api @param {string|number} id */
  party: (api, id) => ({ queryKey: keys.admin.detail('parties', id), queryFn: ({ signal }) => api.admin.parties.get(id, { signal }), retry }),

  /** @param {BlimoApi} api @param {ListParams} params */
  users: (api, params) => ({
    queryKey: keys.admin.list('users', params),
    queryFn: ({ signal }) => api.admin.users.list(params, { signal }),
    placeholderData: keepPrevious,
    retry,
  }),
  /** @param {BlimoApi} api @param {string|number} id */
  user: (api, id) => ({ queryKey: keys.admin.detail('users', id), queryFn: ({ signal }) => api.admin.users.get(id, { signal }), retry }),
  /** G-09: permission catalog with `grantable` for the current user. @param {BlimoApi} api */
  permissionCatalog: (api) => ({
    queryKey: keys.admin.list('permissions'),
    queryFn: ({ signal }) => api.admin.users.permissionCatalog({ signal }),
    staleTime: 10 * 60_000,
    retry,
  }),
  /** @param {BlimoApi} api */
  roles: (api) => ({ queryKey: keys.admin.roles(), queryFn: ({ signal }) => api.admin.roles({ signal }), staleTime: 10 * 60 * 1000, retry }),
  /** @param {BlimoApi} api */
  roleMatrix: (api) => ({
    queryKey: [...keys.admin.roles(), 'matrix'],
    queryFn: ({ signal }) => api.admin.roleMatrix({ signal }),
    staleTime: 10 * 60 * 1000,
    retry,
  }),

  /** @param {BlimoApi} api @param {ListParams} params */
  auditLog: (api, params) => ({
    queryKey: keys.admin.list('audit-log', params),
    queryFn: ({ signal }) => api.admin.auditLog.list(params, { signal }),
    placeholderData: keepPrevious,
    retry,
  }),
  /** @param {BlimoApi} api @param {string|number} id */
  auditLogEntry: (api, id) => ({ queryKey: keys.admin.detail('audit-log', id), queryFn: ({ signal }) => api.admin.auditLog.get(id, { signal }), retry }),
  /** @param {BlimoApi} api */
  auditLogOptions: (api) => ({
    queryKey: keys.admin.list('audit-log-options'),
    queryFn: ({ signal }) => api.admin.auditLog.options({ signal }),
    staleTime: 5 * 60 * 1000,
    retry,
  }),

  /** @param {BlimoApi} api @param {ListParams} params */
  comments: (api, params) => ({
    queryKey: keys.admin.list('comments', params),
    queryFn: ({ signal }) => api.admin.comments.list(params, { signal }),
    placeholderData: keepPrevious,
    retry,
  }),

  /** @param {BlimoApi} api @param {ListParams} params */
  duplicates: (api, params) => ({
    queryKey: keys.admin.list('duplicates', params),
    queryFn: ({ signal }) => api.admin.duplicates.list(params, { signal }),
    placeholderData: keepPrevious,
    retry,
  }),
  /** @param {BlimoApi} api @param {string|number} id */
  duplicate: (api, id) => ({ queryKey: keys.admin.detail('duplicates', id), queryFn: ({ signal }) => api.admin.duplicates.get(id, { signal }), retry }),
  /** @param {BlimoApi} api @param {string|number} id @param {string|number|undefined} partyId */
  duplicatePreview: (api, id, partyId) => ({
    queryKey: keys.admin.sub('duplicates', id, 'merge-preview', { party: String(partyId) }),
    queryFn: ({ signal }) => api.admin.duplicates.preview(id, partyId, { signal }),
    enabled: partyId != null,
    retry,
  }),

  /** @param {BlimoApi} api @param {import('./resources.js').OpenItemParams} [params] */
  financeOpenItems: (api, params = {}) => ({
    queryKey: keys.admin.finance('open-items', params),
    queryFn: ({ signal }) => api.admin.finance.openItems(params, { signal }),
    placeholderData: keepPrevious,
    retry,
  }),
  /** @param {BlimoApi} api @param {import('./resources.js').PeriodParams} [params] */
  financeCosts: (api, params = {}) => ({
    queryKey: keys.admin.finance('costs', params),
    queryFn: ({ signal }) => api.admin.finance.costs(params, { signal }),
    placeholderData: keepPrevious,
    retry,
  }),
  /** G-19: figures per property of the current list page (`finance.view`). @param {BlimoApi} api @param {Array<string|number>} ids */
  financePropertyFigures: (api, ids) => ({
    queryKey: keys.admin.finance('property-figures', { ids: ids.map(String) }),
    queryFn: ({ signal }) => api.admin.finance.propertyFigures(ids, { signal }),
    enabled: ids.length > 0,
    retry,
  }),
  /** G-18: unit types (id, key, label) for the unit edit select. @param {BlimoApi} api */
  unitTypes: (api) => ({ queryKey: keys.admin.list('unit-types'), queryFn: ({ signal }) => api.admin.units.types({ signal }), staleTime: 10 * 60_000, retry }),
  /** @param {BlimoApi} api @param {import('./resources.js').PeriodParams} [params] */
  financeRentDevelopment: (api, params = {}) => ({
    queryKey: keys.admin.finance('rent-development', params),
    queryFn: ({ signal }) => api.admin.finance.rentDevelopment(params, { signal }),
    placeholderData: keepPrevious,
    retry,
  }),

  /** @param {BlimoApi} api @param {ListParams} params */
  importRuns: (api, params) => ({
    queryKey: keys.admin.list('import-runs', params),
    queryFn: ({ signal }) => api.admin.importRuns.list(params, { signal }),
    placeholderData: keepPrevious,
    retry,
  }),
  /** @param {BlimoApi} api */
  importRunsHealth: (api) => ({ queryKey: keys.admin.list('import-runs-health'), queryFn: ({ signal }) => api.admin.importRuns.health({ signal }), retry }),
  /** F-316 @param {BlimoApi} api */
  importSyncTargets: (api) => ({
    queryKey: keys.admin.list('import-runs-sync-targets'),
    queryFn: ({ signal }) => api.admin.importRuns.syncTargets({ signal }),
    retry,
  }),
  /** Personen-Importe (F-104). @param {BlimoApi} api @param {ListParams} params */
  partyImports: (api, params) => ({
    queryKey: keys.admin.list('party-imports', params),
    queryFn: ({ signal }) => api.admin.partyImports.list(params, { signal }),
    placeholderData: keepPrevious,
    retry,
  }),
  /** Full body `{ data, meta }`; polls while queued/processing. @param {BlimoApi} api @param {string|number} id */
  partyImport: (api, id) => ({
    queryKey: keys.admin.detail('party-imports', id),
    queryFn: ({ signal }) => api.admin.partyImports.get(id, { signal }),
    refetchInterval: (/** @type {any} */ query) => (['queued', 'processing'].includes(query.state.data?.data?.status) ? 2000 : false),
    retry,
  }),
  /** @param {BlimoApi} api @param {string|number} id @param {ListParams} params */
  partyImportRows: (api, id, params) => ({
    queryKey: keys.admin.sub('party-imports', id, 'rows', params),
    queryFn: ({ signal }) => api.admin.partyImports.rows(id, params, { signal }),
    placeholderData: keepPrevious,
    retry,
  }),

  /** @param {BlimoApi} api @param {ListParams} params */
  documentChecks: (api, params) => ({
    queryKey: keys.admin.list('document-checks', params),
    queryFn: ({ signal }) => api.admin.documentChecks.list(params, { signal }),
    placeholderData: keepPrevious,
    retry,
  }),
  /** Full body `{ data, meta: { auditors } }`. @param {BlimoApi} api @param {string|number} id */
  documentCheck: (api, id) => ({
    queryKey: keys.admin.detail('document-checks', id),
    queryFn: ({ signal }) => api.admin.documentChecks.get(id, { signal }),
    retry,
  }),
  /** @param {BlimoApi} api @param {string|number} id */
  documentCheckComments: (api, id) => ({
    queryKey: keys.admin.sub('document-checks', id, 'comments'),
    queryFn: ({ signal }) => api.admin.documentChecks.comments(id, { signal }),
    retry,
  }),
  /** Folders of one owner scope. @param {BlimoApi} api @param {{ owner_type: 'property'|'unit'|'global', owner_id?: string|number }} params */
  documentFolders: (api, params) => ({
    queryKey: keys.admin.list('document-folders', params),
    queryFn: ({ signal }) => api.admin.documentFolders.list(params, { signal }),
    retry,
  }),
  /** @param {BlimoApi} api @param {string|number} folderId */
  folderDocuments: (api, folderId) => ({
    queryKey: keys.admin.sub('document-folders', folderId, 'documents'),
    queryFn: ({ signal }) => api.admin.documentFolders.documents(folderId, { signal }),
    retry,
  }),

  /** @param {BlimoApi} api @param {ListParams} params */
  announcements: (api, params) => ({
    queryKey: keys.admin.list('announcements', params),
    queryFn: ({ signal }) => api.admin.announcements.list(params, { signal }),
    placeholderData: keepPrevious,
    retry,
  }),
  /** @param {BlimoApi} api @param {string|number} id */
  announcement: (api, id) => ({
    queryKey: keys.admin.detail('announcements', id),
    queryFn: ({ signal }) => api.admin.announcements.get(id, { signal }),
    retry,
  }),
  /** Always fresh: the recipients may change between two dispatches. @param {BlimoApi} api @param {string|number} id */
  announcementRecipients: (api, id) => ({
    queryKey: keys.admin.sub('announcements', id, 'recipients'),
    queryFn: ({ signal }) => api.admin.announcements.recipients(id, { signal }),
    staleTime: 0,
    gcTime: 0,
    retry,
  }),

  /** @param {BlimoApi} api @param {ListParams} params */
  messages: (api, params) => ({
    queryKey: keys.admin.list('messages', params),
    queryFn: ({ signal }) => api.admin.messages.list(params, { signal }),
    placeholderData: keepPrevious,
    retry,
  }),
  /** Number of open threads (`meta.open` of the list) for the navigation badge. @param {BlimoApi} api */
  messagesOpenCount: (api) => ({
    queryKey: [...keys.admin.resource('messages'), 'open-count'],
    queryFn: async ({ signal }) => (await api.admin.messages.list({ per_page: 1 }, { signal }))?.meta?.open ?? 0,
    staleTime: 60_000,
    retry,
  }),
  /** @param {BlimoApi} api @param {string|number} id */
  message: (api, id) => ({ queryKey: keys.admin.detail('messages', id), queryFn: ({ signal }) => api.admin.messages.get(id, { signal }), retry }),

  /** @param {BlimoApi} api @param {ListParams} params */
  outboundMessages: (api, params) => ({
    queryKey: keys.admin.list('outbound-messages', params),
    queryFn: ({ signal }) => api.admin.outboundMessages.list(params, { signal }),
    placeholderData: keepPrevious,
    retry,
  }),
  /** @param {BlimoApi} api @param {string|number} id */
  outboundMessage: (api, id) => ({
    queryKey: keys.admin.detail('outbound-messages', id),
    queryFn: ({ signal }) => api.admin.outboundMessages.get(id, { signal }),
    retry,
  }),

  /** @param {BlimoApi} api */
  mailTemplates: (api) => ({ queryKey: keys.admin.list('mail-templates'), queryFn: ({ signal }) => api.admin.mailTemplates.list({ signal }), retry }),
  /** @param {BlimoApi} api @param {string} key */
  mailTemplate: (api, key) => ({
    queryKey: keys.admin.detail('mail-templates', key),
    queryFn: ({ signal }) => api.admin.mailTemplates.get(key, { signal }),
    retry,
  }),
  /**
   * Preview with sample values: the stored template, or – with `draft` – the unsaved subject/body (live preview).
   * @param {BlimoApi} api @param {string} key @param {{ subject?: string, body_html?: string }} [draft]
   */
  mailTemplatePreview: (api, key, draft) => ({
    queryKey: keys.admin.sub('mail-templates', key, 'preview', draft ? { subject: draft.subject, body_html: draft.body_html } : {}),
    queryFn: () => (draft ? api.admin.mailTemplates.preview(key, undefined, draft) : api.admin.mailTemplates.preview(key)),
    placeholderData: keepPrevious,
    retry,
  }),
  /** Assignable users for the inbox. @param {BlimoApi} api */
  messageAssignees: (api) => ({
    queryKey: [...keys.admin.resource('messages'), 'assignees'],
    queryFn: ({ signal }) => api.admin.messages.assignees({ signal }),
    staleTime: 5 * 60 * 1000,
    retry,
  }),
  /** 404 = no booking list imported. @param {BlimoApi} api @param {string|number} id @param {{ search?: string, page?: number }} [params] */
  documentCheckBookingList: (api, id, params = {}) => ({
    queryKey: keys.admin.sub('document-checks', id, 'booking-list', params),
    queryFn: ({ signal }) => api.admin.documentChecks.bookingList(id, params, { signal }),
    placeholderData: keepPrevious,
    retry,
  }),

  /** @param {BlimoApi} api @param {ListParams} params */
  meters: (api, params) => ({
    queryKey: keys.admin.list('meters', params),
    queryFn: ({ signal }) => api.admin.meters.list(params, { signal }),
    placeholderData: keepPrevious,
    retry,
  }),
  /** @param {BlimoApi} api @param {string|number} id */
  meter: (api, id) => ({ queryKey: keys.admin.detail('meters', id), queryFn: ({ signal }) => api.admin.meters.get(id, { signal }), retry }),
  /** @param {BlimoApi} api */
  meterSuppliers: (api) => ({
    queryKey: keys.admin.list('meter-suppliers'),
    queryFn: ({ signal }) => api.admin.meterSuppliers.list({ signal }),
    staleTime: 5 * 60 * 1000,
    retry,
  }),
  /** Full body incl. `meta.summary`. @param {BlimoApi} api @param {number|string} year @param {ListParams} [params] */
  meterReadings: (api, year, params = {}) => ({
    queryKey: keys.admin.list('meter-readings', { year: String(year), ...params }),
    queryFn: ({ signal }) => api.admin.meterReadings.list(year, params, { signal }),
    placeholderData: keepPrevious,
    retry,
  }),
  /** Single reader mail draft – always fresh. @param {BlimoApi} api @param {number|string} year @param {string|number} partyId */
  meterReaderMailDraft: (api, year, partyId) => ({
    queryKey: [...keys.admin.resource('meter-readings'), 'reader-mail', String(year), String(partyId)],
    queryFn: ({ signal }) => api.admin.meterYears.readerMailDraft(year, partyId, { signal }),
    staleTime: 0,
    gcTime: 0,
    retry,
  }),

  /** @param {BlimoApi} api @param {ListParams} params */
  proxyAuthorizations: (api, params) => ({
    queryKey: keys.admin.list('proxy-authorizations', params),
    queryFn: ({ signal }) => api.admin.proxyAuthorizations.list(params, { signal }),
    placeholderData: keepPrevious,
    retry,
  }),
  /** @param {BlimoApi} api @param {ListParams} params */
  lettingTickets: (api, params) => ({
    queryKey: keys.admin.list('letting-tickets', params),
    queryFn: ({ signal }) => api.admin.lettingTickets.list(params, { signal }),
    placeholderData: keepPrevious,
    retry,
  }),
  /** Mandanten (F-013), full paginator body. @param {BlimoApi} api @param {ListParams} params */
  mandates: (api, params) => ({
    queryKey: keys.admin.list('mandates', params),
    queryFn: ({ signal }) => api.admin.mandates.list(params, { signal }),
    placeholderData: keepPrevious,
    retry,
  }),
  /** @param {BlimoApi} api @param {string|number} id */
  mandate: (api, id) => ({ queryKey: keys.admin.detail('mandates', id), queryFn: ({ signal }) => api.admin.mandates.get(id, { signal }), retry }),
  /** Property pick list with the client's assignment counts. @param {BlimoApi} api @param {string|number} id @param {{ q?: string }} [params] */
  mandateProperties: (api, id, params = {}) => ({
    queryKey: keys.admin.sub('mandates', id, 'properties', params),
    queryFn: ({ signal }) => api.admin.mandates.properties(id, params, { signal }),
    placeholderData: keepPrevious,
    retry,
  }),
  /** Units + cost accounts of one property for the client. @param {BlimoApi} api @param {string|number} id @param {string|number} propertyId */
  /** @param {BlimoApi} api @param {string|number} id @param {{ month?: string, user_id?: number }} [params] */
  mandateReportPreview: (api, id, params = {}) => ({
    queryKey: keys.admin.sub('mandates', id, 'report-preview', params),
    queryFn: ({ signal }) => api.admin.mandates.reportPreview(id, params, { signal }),
    retry,
  }),
  /** Zahlungsverlauf (finance.view). @param {BlimoApi} api @param {string|number} id */
  tenancyPaymentHistory: (api, id) => ({
    queryKey: keys.admin.sub('tenancies', id, 'payment-history'),
    queryFn: ({ signal }) => api.admin.tenancies.paymentHistory(id, {}, { signal }),
    retry,
  }),
  mandateProperty: (api, id, propertyId) => ({
    queryKey: keys.admin.sub('mandates', id, 'property', { propertyId: String(propertyId) }),
    queryFn: ({ signal }) => api.admin.mandates.property(id, propertyId, { signal }),
    retry,
  }),
  /** Full body `{ data: categories, meta: { uncategorized } }`. @param {BlimoApi} api */
  links: (api) => ({ queryKey: keys.admin.list('links'), queryFn: ({ signal }) => api.admin.links.list({ signal }), retry }),
  /** Einstellungen (full body incl. meta). @param {BlimoApi} api */
  settings: (api) => ({ queryKey: keys.admin.settings(), queryFn: ({ signal }) => api.admin.settings.get({ signal }), retry }),
};

export const portalQueries = {
  /** @param {BlimoApi} api */
  properties: (api) => ({ queryKey: keys.portal.list('properties'), queryFn: ({ signal }) => api.portal.properties.list({ signal }), retry }),
  /** @param {BlimoApi} api @param {string|number} id */
  property: (api, id) => ({ queryKey: keys.portal.detail('properties', id), queryFn: ({ signal }) => api.portal.properties.get(id, { signal }), retry }),
  /** 404 = user is not an owner in this property (the tab is hidden then). @param {BlimoApi} api @param {string|number} id */
  propertyOwners: (api, id) => ({
    queryKey: keys.portal.sub('properties', id, 'owners'),
    queryFn: ({ signal }) => api.portal.properties.owners(id, { signal }),
    retry,
  }),
  /** @param {BlimoApi} api @param {string|number} id */
  unit: (api, id) => ({ queryKey: keys.portal.detail('units', id), queryFn: ({ signal }) => api.portal.units.get(id, { signal }), retry }),
  /** 404 = no tenancy access for this unit (the section is hidden then). @param {BlimoApi} api @param {string|number} id */
  /** 404 = no access (the timeline is hidden then). @param {BlimoApi} api @param {string|number} id */
  unitPaymentHistory: (api, id) => ({
    queryKey: keys.portal.sub('units', id, 'payment-history'),
    queryFn: ({ signal }) => api.portal.units.paymentHistory(id, { signal }),
    retry,
  }),
  unitTenancy: (api, id) => ({
    queryKey: keys.portal.sub('units', id, 'tenancy'),
    queryFn: ({ signal }) => api.portal.units.tenancy(id, { signal }),
    retry,
  }),
  /** Tiles (only keys the user may see) + contacts. @param {BlimoApi} api */
  home: (api) => ({ queryKey: keys.portal.home(), queryFn: ({ signal }) => api.portal.home({ signal }), staleTime: 60_000, retry }),
  /** @param {BlimoApi} api */
  profile: (api) => ({ queryKey: keys.portal.profile(), queryFn: ({ signal }) => api.portal.profile.get({ signal }), retry }),
  /** Paginator body. @param {BlimoApi} api @param {ListParams} [params] */
  tenancies: (api, params = {}) => ({
    queryKey: keys.portal.list('tenancies', params),
    queryFn: ({ signal }) => api.portal.tenancies.list(params, { signal }),
    placeholderData: keepPrevious,
    retry,
  }),

  /** G-19 / F-125: figures per visible property (403 without `finance.view`). @param {BlimoApi} api */
  financeProperties: (api) => ({ queryKey: keys.portal.finance('properties'), queryFn: ({ signal }) => api.portal.finance.properties({ signal }), retry }),
  /** 403 = no `finance.view` (tiles are hidden then). @param {BlimoApi} api */
  financeSummary: (api) => ({ queryKey: keys.portal.finance('summary'), queryFn: ({ signal }) => api.portal.finance.summary({ signal }), retry }),
  /** @param {BlimoApi} api @param {import('./resources.js').PeriodParams} [params] */
  financeRentDevelopment: (api, params = {}) => ({
    queryKey: keys.portal.finance('rent-development', params),
    queryFn: ({ signal }) => api.portal.finance.rentDevelopment(params, { signal }),
    placeholderData: keepPrevious,
    retry,
  }),
  /** @param {BlimoApi} api @param {import('./resources.js').PeriodParams} [params] */
  financeCosts: (api, params = {}) => ({
    queryKey: keys.portal.finance('costs', params),
    queryFn: ({ signal }) => api.portal.finance.costs(params, { signal }),
    placeholderData: keepPrevious,
    retry,
  }),
  /** @param {BlimoApi} api @param {import('./resources.js').OpenItemParams} [params] */
  financeOpenItems: (api, params = {}) => ({
    queryKey: keys.portal.finance('open-items', params),
    queryFn: ({ signal }) => api.portal.finance.openItems(params, { signal }),
    placeholderData: keepPrevious,
    retry,
  }),
  /** Lives under the finance key: a baseline change invalidates it together with all finance views. @param {BlimoApi} api @param {string|number} id */
  accumulatedDevelopment: (api, id) => ({
    queryKey: keys.portal.finance('accumulated-development', { property: String(id) }),
    queryFn: ({ signal }) => api.portal.properties.accumulatedDevelopment(id, { signal }),
    retry,
  }),
  /** @param {BlimoApi} api @param {string|number} id */
  investmentBaseline: (api, id) => ({
    queryKey: keys.portal.finance('investment-baseline', { property: String(id) }),
    queryFn: ({ signal }) => api.portal.properties.investmentBaseline(id, { signal }),
    retry,
  }),

  /** @param {BlimoApi} api */
  documentChecks: (api) => ({ queryKey: keys.portal.list('document-checks'), queryFn: ({ signal }) => api.portal.documentChecks.list({}, { signal }), retry }),
  /** @param {BlimoApi} api @param {string|number} id */
  documentCheck: (api, id) => ({
    queryKey: keys.portal.detail('document-checks', id),
    queryFn: ({ signal }) => api.portal.documentChecks.get(id, { signal }),
    retry,
  }),
  /** 404 = not an auditor / no list (the section is hidden then). @param {BlimoApi} api @param {string|number} id @param {{ page?: number, per_page?: number }} [params] */
  documentCheckBookingList: (api, id, params = {}) => ({
    queryKey: keys.portal.sub('document-checks', id, 'booking-list', params),
    queryFn: ({ signal }) => api.portal.documentChecks.bookingList(id, params, { signal }),
    placeholderData: keepPrevious,
    retry,
  }),
  /** @param {BlimoApi} api @param {string|number} id @param {{ document: string, compare?: string }} params */
  documentCheckComments: (api, id, params) => ({
    queryKey: keys.portal.sub('document-checks', id, 'comments', params),
    queryFn: ({ signal }) => api.portal.documentChecks.comments(id, params, { signal }),
    retry,
  }),

  /** Full body incl. `meta.unread`. @param {BlimoApi} api @param {{ page?: number }} [params] */
  announcements: (api, params = {}) => ({
    queryKey: keys.portal.list('announcements', params),
    queryFn: ({ signal }) => api.portal.announcements.list(params, { signal }),
    placeholderData: keepPrevious,
    retry,
  }),
  /** Opening marks it read on the server. @param {BlimoApi} api @param {string|number} id */
  announcement: (api, id) => ({
    queryKey: keys.portal.detail('announcements', id),
    queryFn: ({ signal }) => api.portal.announcements.get(id, { signal }),
    retry,
  }),

  /** @param {BlimoApi} api @param {{ page?: number }} [params] */
  messages: (api, params = {}) => ({
    queryKey: keys.portal.list('messages', params),
    queryFn: ({ signal }) => api.portal.messages.list(params, { signal }),
    placeholderData: keepPrevious,
    retry,
  }),
  /** Opening marks replies read on the server. @param {BlimoApi} api @param {string|number} id */
  message: (api, id) => ({ queryKey: keys.portal.detail('messages', id), queryFn: ({ signal }) => api.portal.messages.get(id, { signal }), retry }),

  /** Full body `{ data: groups, meta: { year, open } }`. @param {BlimoApi} api @param {{ year?: number|string }} [params] */
  meterReadings: (api, params = {}) => ({
    queryKey: keys.portal.list('meter-readings', params),
    queryFn: ({ signal }) => api.portal.meterReadings.list(params, { signal }),
    retry,
  }),
  /** @param {BlimoApi} api @param {{ all?: boolean }} [params] */
  proxyAuthorizations: (api, params = {}) => ({
    queryKey: keys.portal.list('proxy-authorizations', params),
    queryFn: ({ signal }) => api.portal.proxyAuthorizations.list(params, { signal }),
    placeholderData: keepPrevious,
    retry,
  }),
  /** @param {BlimoApi} api @param {string|number} id */
  proxyAuthorization: (api, id) => ({
    queryKey: keys.portal.detail('proxy-authorizations', id),
    queryFn: ({ signal }) => api.portal.proxyAuthorizations.get(id, { signal }),
    retry,
  }),
  /** G-21 Vermietungsübersicht. @param {BlimoApi} api @param {{ vat?: 'net'|'gross', open?: boolean, property?: string|number }} [params] */
  lettingOverview: (api, params = {}) => ({
    queryKey: keys.portal.list('letting-overview', params),
    queryFn: ({ signal }) => api.portal.lettingOverview.get(params, { signal }),
    placeholderData: keepPrevious,
    retry,
  }),
  /** @param {BlimoApi} api @param {{ open?: boolean }} [params] */
  lettingTickets: (api, params = {}) => ({
    queryKey: keys.portal.list('letting-tickets', params),
    queryFn: ({ signal }) => api.portal.lettingTickets.list(params, { signal }),
    placeholderData: keepPrevious,
    retry,
  }),
  /** @param {BlimoApi} api */
  links: (api) => ({ queryKey: keys.portal.list('links'), queryFn: ({ signal }) => api.portal.links.list({ signal }), staleTime: 10 * 60 * 1000, retry }),
  /** Highlighted links (home page, F-1005). @param {BlimoApi} api */
  highlightedLinks: (api) => ({
    queryKey: keys.portal.list('links', { highlighted: true }),
    queryFn: ({ signal }) => api.portal.links.highlighted({ signal }),
    staleTime: 10 * 60 * 1000,
    retry,
  }),

  /** Folders with documents. @param {BlimoApi} api @param {{ property?: string|number, unit?: string|number, scope?: 'global' }} [params] */
  documents: (api, params = {}) => ({
    queryKey: keys.portal.list('documents', params),
    queryFn: ({ signal }) => api.portal.documents.list(params, { signal }),
    placeholderData: keepPrevious,
    retry,
  }),
  /** Volltextsuche; disabled below 2 characters. @param {BlimoApi} api @param {string} q */
  documentSearch: (api, q) => ({
    queryKey: keys.portal.list('documents', { search: q }),
    queryFn: ({ signal }) => api.portal.documents.search(q, {}, { signal }),
    enabled: q.trim().length >= 2,
    placeholderData: keepPrevious,
    retry,
  }),

  /** Blimo AI availability (nav entry, empty state, suggestions). @param {BlimoApi} api */
  assistantStatus: (api) => ({
    queryKey: keys.portal.assistant('status'),
    queryFn: ({ signal }) => api.portal.assistant.status({ signal }),
    staleTime: 5 * 60 * 1000,
    retry,
  }),
  /** @param {BlimoApi} api */
  assistantConversations: (api) => ({
    queryKey: keys.portal.assistant('conversations'),
    queryFn: ({ signal }) => api.portal.assistant.conversations.list({ signal }),
    retry,
  }),
  /** @param {BlimoApi} api @param {string|number} id */
  assistantConversation: (api, id) => ({
    queryKey: keys.portal.assistant('conversation', id),
    queryFn: ({ signal }) => api.portal.assistant.conversations.get(id, { signal }),
    retry,
  }),
  /** @param {BlimoApi} api */
  assistantMcpTokens: (api) => ({
    queryKey: keys.portal.assistant('mcp-tokens'),
    queryFn: ({ signal }) => api.portal.assistant.mcpTokens.list({ signal }),
    retry,
  }),
};

/** Keep showing the previous page while the next one loads (TanStack `keepPreviousData`). */
function keepPrevious(/** @type {any} */ previous) {
  return previous;
}

export const publicQueries = {
  /**
   * Runtime branding for both apps (login pages included). Long staleTime – it rarely changes; the settings page
   * invalidates `keys.branding()` after saving. Never throws into the UI (defaults stay on errors).
   * @param {BlimoApi} api
   */
  branding: (api) => ({
    queryKey: keys.branding(),
    queryFn: ({ signal }) => api.public.branding({ signal }),
    staleTime: 30 * 60 * 1000,
    gcTime: Infinity,
    retry: 1,
    refetchOnWindowFocus: false,
  }),
};
